const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('authToken');
  }

  // Set auth token
  setAuthToken(token) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  // Clear auth token
  clearAuthToken() {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  // Get auth headers
  getAuthHeaders(isFormData = false) {
    const headers = {};
    
    // Only set Content-Type for JSON, not for FormData (browser sets it automatically)
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    if (this.token) {
      headers['Authorization'] = `Token ${this.token}`;
    }
    
    return headers;
  }

  // Generic API request method
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Check if body is FormData
    const isFormData = options.body instanceof FormData;
    
    const config = {
      headers: {
        ...this.getAuthHeaders(isFormData),
        ...(options.headers || {}), // Allow headers to be overridden
      },
      ...options,
    };
    
    // Remove Content-Type from headers if it's FormData (let browser set it with boundary)
    if (isFormData && config.headers['Content-Type']) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');
      
      // If no content or not JSON, return empty object
      if (contentLength === '0' || response.status === 204 || !contentType?.includes('application/json')) {
        return {};
      }

      // Try to parse JSON, return empty object if it fails
      try {
        return await response.json();
      } catch (jsonError) {
        console.warn('Failed to parse JSON response:', jsonError);
        return {};
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // AUTH ENDPOINTS
  async login(email, password) {
    const response = await this.request('/auth/token/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.auth_token) {
      this.setAuthToken(response.auth_token);
    }
    
    return response;
  }

  async logout() {
    try {
      await this.request('/auth/token/logout/', {
        method: 'POST',
      });
    } finally {
      this.clearAuthToken();
    }
  }

  async getUserProfile() {
    return this.request('/accounts/profile/');
  }

  async updateProfile(profileData) {
    return this.request('/accounts/profile/', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async changePassword(oldPassword, newPassword) {
    return this.request('/accounts/password/change/', {
      method: 'POST',
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
        re_new_password: newPassword
      }),
    });
  }

  // STUDENT ENDPOINTS
  async getStudents(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await this.request(`/accounts/students/?${searchParams}`);
    // Handle paginated response - return the results array and pagination info
    return response.results || response;
  }

  async createStudent(studentData) {
    return this.request('/accounts/students/', {
      method: 'POST',
      body: JSON.stringify(studentData),
    });
  }

  async updateStudent(studentId, studentData) {
    return this.request(`/accounts/students/${studentId}/`, {
      method: 'PUT',
      body: JSON.stringify(studentData),
    });
  }

  async deleteStudent(studentId) {
    return this.request(`/accounts/students/${studentId}/`, {
      method: 'DELETE',
    });
  }

  async confirmStudentPayment(studentId) {
    return this.request(`/accounts/students/${studentId}/confirm_payment/`, {
      method: 'POST',
    });
  }

  async resetStudentPassword(studentId, options = {}) {
    return this.request(`/accounts/students/${studentId}/reset_password/`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async getPendingPayments() {
    return this.request('/accounts/students/pending_payments/');
  }

  // TEACHER ENDPOINTS
  async getTeachers(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await this.request(`/accounts/teachers/?${searchParams}`);
    // Handle paginated response - return the results array
    return response.results || response;
  }

  async createTeacher(teacherData) {
    return this.request('/accounts/teachers/', {
      method: 'POST',
      body: JSON.stringify(teacherData),
    });
  }

  async updateTeacher(teacherId, teacherData) {
    return this.request(`/accounts/teachers/${teacherId}/`, {
      method: 'PUT',
      body: JSON.stringify(teacherData),
    });
  }

  async deleteTeacher(teacherId) {
    return this.request(`/accounts/teachers/${teacherId}/`, {
      method: 'DELETE',
    });
  }

  async resetTeacherPassword(teacherId, data = {}) {
    return this.request(`/accounts/teachers/${teacherId}/reset_password/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTeacherSpecializations() {
    return this.request('/accounts/teachers/specializations/');
  }

  // COURSE ENDPOINTS
  async getCourses(params = {}) {
    // For admin interface, fetch all courses by paginating through all pages
    let allCourses = [];
    let currentPage = 1;
    let hasMore = true;
    
    while (hasMore) {
      const pageParams = { ...params, page: currentPage };
      const searchParams = new URLSearchParams(pageParams);
      const response = await this.request(`/courses/courses/?${searchParams}`);
      
      if (response.results && response.results.length > 0) {
        allCourses = allCourses.concat(response.results);
        hasMore = !!response.next; // Continue if there's a next page
        currentPage++;
      } else {
        hasMore = false;
      }
    }
    
    return allCourses;
  }

  async createCourse(courseData) {
    // Handle file uploads
    const formData = new FormData();
    Object.keys(courseData).forEach(key => {
      if (courseData[key] !== null && courseData[key] !== undefined) {
        formData.append(key, courseData[key]);
      }
    });

    return this.request('/courses/courses/', {
      method: 'POST',
      headers: {
        'Authorization': `Token ${this.token}`,
        // Don't set Content-Type for FormData, let browser handle it
      },
      body: formData,
    });
  }

  async updateCourse(courseId, courseData) {
    return this.request(`/courses/courses/${courseId}/`, {
      method: 'PUT',
      headers: {
        'Authorization': `Token ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(courseData),
    });
  }

  async deleteCourse(courseId) {
    return this.request(`/courses/courses/${courseId}/`, {
      method: 'DELETE',
    });
  }

  async getCourseModules(courseId) {
    return this.request(`/courses/${courseId}/modules/`);
  }

  async getCourseBatches(courseId) {
    return this.request(`/courses/${courseId}/batches/`);
  }

  // MODULE ENDPOINTS
  async getModules(params = {}) {
    const searchParams = new URLSearchParams(params);
    return this.request(`/courses/modules/?${searchParams}`);
  }

  async createModule(moduleData) {
    return this.request('/courses/modules/', {
      method: 'POST',
      body: JSON.stringify(moduleData),
    });
  }

  async updateModule(moduleId, moduleData) {
    return this.request(`/courses/modules/${moduleId}/`, {
      method: 'PUT',
      body: JSON.stringify(moduleData),
    });
  }

  async deleteModule(moduleId) {
    return this.request(`/courses/modules/${moduleId}/`, {
      method: 'DELETE',
    });
  }

  async getModuleMaterials(moduleId) {
    return this.request(`/courses/modules/${moduleId}/materials/`);
  }

  // BATCH ENDPOINTS
  async getBatches(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await this.request(`/batches/batches/?${searchParams}`);
    // Handle paginated response - return the results array
    return response.results || response;
  }

  async createBatch(batchData) {
    return this.request('/batches/batches/', {
      method: 'POST',
      body: JSON.stringify(batchData),
    });
  }

  async updateBatch(batchId, batchData) {
    return this.request(`/batches/batches/${batchId}/`, {
      method: 'PUT',
      body: JSON.stringify(batchData),
    });
  }

  async deleteBatch(batchId) {
    return this.request(`/batches/batches/${batchId}/`, {
      method: 'DELETE',
    });
  }

  async enrollStudent(batchId, studentId) {
    return this.request(`/batches/batches/${batchId}/enroll_student/`, {
      method: 'POST',
      body: JSON.stringify({ student: studentId }),
    });
  }

  async getBatchDetails(batchId) {
    return this.request(`/batches/batches/${batchId}/`);
  }

  async getBatchStudents(batchId) {
    return this.request(`/batches/batches/${batchId}/students/`);
  }

  // MATERIAL ENDPOINTS  
  async getMaterials(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await this.request(`/materials/materials/?${searchParams}`);
    // Handle paginated response - return the results array
    return response.results || response;
  }

  async createMaterial(materialData) {
    // Handle file uploads
    const formData = new FormData();
    Object.keys(materialData).forEach(key => {
      if (materialData[key] !== null && materialData[key] !== undefined) {
        formData.append(key, materialData[key]);
      }
    });

    return this.request('/materials/materials/', {
      method: 'POST',
      headers: {
        'Authorization': `Token ${this.token}`,
        // Don't set Content-Type for FormData
      },
      body: formData,
    });
  }

  async updateMaterial(materialId, materialData) {
    return this.request(`/materials/materials/${materialId}/`, {
      method: 'PUT',
      body: JSON.stringify(materialData),
    });
  }

  async deleteMaterial(materialId) {
    return this.request(`/materials/materials/${materialId}/`, {
      method: 'DELETE',
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService; 